import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../../api";
import { AreaChart } from "../../../components/chart";
import { Button, ButtonRow } from "../../../components/button";
import { Card, CardContent, CardHeader, CardNavigation } from "../../../components/card";
import { CommandBar } from "../../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../../components/dashboard";
import { DateTimeLabel } from "../../../components/datetime-label";
import { Dropdown, DropdownDivider, DropdownItem } from "../../../components/dropdown";
import { FileInput } from "../../../components/file-input";
import { FormControl, Select, TextArea, TextInput } from "../../../components/form";
import { Icon } from "../../../components/icon";
import { LogRowItem } from "../../../components/log-row-item";
import { SegmentBar } from "../../../components/progress-bar";
import { TabControl } from "../../../components/tabcontrol";
import { DefaultRow, Table, TableActionCell, TableBody, TableCell, TableHeader, TableHeaderActionCell, TableHeaderCell, TableRow } from "../../../components/table";
import { BlockCodeWrapper, CopiableText, Heading, MutedText, TextViewer } from "../../../components/text";
import { displayAlert, displayFormControl } from "../../../ui/alert";
import { copyToClipboard } from "../../../ui/clipboard";
import { throttledCall } from "../../../ui/throttle";
import { runOperation } from "../../../ui/operation";
import { formatCurrency, formatDuration, formatNumber, formatPreciseCurrency, formatVeryPreciseCurrency } from "../../../ui/format";
import {
    BatchItemStates,
    confirmDanger,
    runtimeLabel,
    StateBadge
} from "../shared";
import { Section } from "../../../components/layout";
import { Link } from "../../../components/link";
import { getSession } from "../../../session";
import { JsonControl } from "../../../components/json-control";
import { pipe } from "../../../util";
import { Definition, DefinitionList } from "../../../components/definition";
import { PermutationGenerator } from "../../../components/permutation-generator";

export default function (ctx) {
    const workflowId = ctx.params.workflowId;
    const jobId = ctx.params.jobId;
    const jobData = el.state.create(null);
    const items = el.state.create([]);
    const itemFilter = el.state.create("");
    const stateFilter = el.state.create("");
    const itemSort = el.state.create("inserted-desc");

    const GridOptions = {
        style: {
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '6px 15px'
        }
    };

    async function reloadItems() {
        const query = new URLSearchParams();
        const hasFilter = itemFilter.value || stateFilter.value;
        query.set("limit", hasFilter ? "300" : "200");
        query.set("sort", itemSort.value);
        if (itemFilter.value)
            query.set("filter", itemFilter.value);
        if (stateFilter.value?.startsWith("confidence:"))
            query.set("confidence", stateFilter.value.slice("confidence:".length));
        else if (stateFilter.value)
            query.set("state", stateFilter.value);

        const response = await api.getJson(`/api/v1/batch/workflows/${workflowId}/jobs/${jobId}/items?${query.toString()}`);
        items.value = response.data || [];
    }

    async function reloadAll() {
        await runOperation("Loading batch job...", async () => {
            const [jobResponse] = await Promise.all([
                api.getJson(`/api/v1/batch/workflows/${workflowId}/jobs/${jobId}`),
                reloadItems()
            ]);
            jobData.value = jobResponse.data;
        });
    }

    function viewItem(itemId) {
        runOperation("Loading item details...", async () => {
            const response = await api.getJson(`/api/v1/batch/workflows/${workflowId}/jobs/${jobId}/items/${itemId}`);
            const detail = response.data;

            displayAlert({
                title: "Batch item details",
                dialogSize: "large",
                message: TabControl([
                    {
                        title: "Metadata",
                        contents: el.fragment(
                            Card(
                                CardHeader("Item information"),
                                CardContent(
                                    GridOptions,
                                    LogRowItem("ID", CopiableText(detail.id)),
                                    LogRowItem("Inserted at", DateTimeLabel(detail.insertedAt)),
                                    LogRowItem("Processed at", detail.processedAt ? DateTimeLabel(detail.processedAt) : MutedText("-")),
                                    LogRowItem("State", StateBadge(detail.state)),
                                    LogRowItem("Priority", formatNumber(detail.priority || 0)),
                                    LogRowItem("Cost", formatVeryPreciseCurrency(detail.totalCost)),
                                    LogRowItem("Confidence", detail.confidence == null ? MutedText("-") : `${formatNumber(Number(detail.confidence) * 100)}%`)
                                )
                            ),
                            detail.validationResult && Card(
                                CardHeader("Validation"),
                                CardContent(
                                    GridOptions,
                                    LogRowItem("Passed", detail.validationResult.passed ? "Yes" : "No"),
                                    LogRowItem("Reason", detail.validationResult.reason || MutedText("-"))
                                )
                            )
                        )
                    },
                    {
                        title: "Input content",
                        contents: BlockCodeWrapper("Line input", detail.input || "")
                    },
                    {
                        title: "Output",
                        contents: pipe(detail.output, output => {
                            try {
                                const parsed = JSON.parse(output);
                                return BlockCodeWrapper("Line input", JSON.stringify(parsed, null, 4), "json");
                            } catch {
                                return BlockCodeWrapper("Line input", output || "");
                            }
                        })
                    },
                ])
            });
        });
    }

    function removeItems(mode, message) {
        confirmDanger({
            message,
            buttonText: "Remove items",
            operationText: "Removing items...",
            async onConfirm() {
                await api.deleteJson(`/api/v1/batch/workflows/${workflowId}/jobs/${jobId}/items?mode=${mode}`);
                await reloadAll();
            }
        });
    }

    function retryItems(mode) {
        runOperation("Retrying items...", async () => {
            await api.postJson(`/api/v1/batch/workflows/${workflowId}/jobs/${jobId}/items/retry?mode=${mode}`, {});
            await reloadAll();
        });
    }

    function exportItems(query) {
        const exportQuery = new URLSearchParams(query);
        exportQuery.set("api-key", getSession().sessionKey);
        window.open(`${window.env.apiEndpoint}/api/v1/batch/workflows/${workflowId}/jobs/${jobId}/export.jsonl?${exportQuery.toString()}`, "_blank", "noopener");
    }

    function insertItems(formData, operationText) {
        runOperation(operationText, async () => {
            const response = await api.fetch(`/api/v1/batch/workflows/${workflowId}/jobs/${jobId}/items`, {
                method: "POST",
                body: formData
            });
            await api.parseBody(response);
            await reloadAll();
        });
    }

    reloadAll();

    return Dashboard(
        Section(
            Link(el.fragment(Icon("arrow-left-line"), "Back "), null, {
                onClick() {
                    history.go(-1);
                }
            })
        ),
        el.state.listening([jobData], () =>
            DashboardTitle(jobData.value?.job.title, "Monitor processing, import items and export results."),
        ),
        Section(
            CommandBar(
                Button(Icon("refresh-line"), "Refresh", {
                    onClick() {
                        reloadAll();
                    }
                }),
                el.state.listening([jobData], () => {
                    const job = jobData.value?.job;
                    const isActive = job?.state[0] === "Active";

                    return Button(
                        Icon(isActive ? "pause-line" : "play-line"),
                        isActive ? "Pause job" : "Start job",
                        {
                            onClick(event) {
                                const nextState = isActive ? "Paused" : "Active";
                                event.target.setAttribute("disabled", true);
                                runOperation(nextState === "Active" ? "Starting job..." : "Pausing job...", async () => {
                                    await api.patchJson(`/api/v1/batch/workflows/${workflowId}/jobs/${jobId}`, {
                                        state: nextState
                                    });
                                    await reloadAll();
                                });
                            }
                        }
                    );
                }),
                Dropdown("Export",
                    DropdownItem("Successful", Icon("checkbox-circle-line"), {
                        onClick() {
                            exportItems("state=successful");
                        }
                    }),
                    DropdownDivider(),
                    DropdownItem("High confidence", Icon("arrow-up-circle-line"), {
                        onClick() {
                            exportItems("state=successful&confidence=high");
                        }
                    }),
                    DropdownItem("Low confidence", Icon("arrow-down-circle-line"), {
                        onClick() {
                            exportItems("state=successful&confidence=low");
                        }
                    }),
                    DropdownDivider(),
                    DropdownItem("Failed", Icon("error-warning-line"), {
                        onClick() {
                            exportItems("state=failed");
                        }
                    }),
                    DropdownDivider(),
                    DropdownItem("All processed", Icon("download-line"), {
                        onClick() {
                            exportItems("state=all");
                        }
                    }),
                    DropdownDivider(),
                    DropdownItem("Copy line schema", Icon("file-copy-line"), {
                        onClick() {
                            runOperation("Copying line schema...", async () => {
                                const workflowResponse = await api.getJson(`/api/v1/batch/workflows/${workflowId}`);
                                const resultSchema = workflowResponse.data?.options?.resultSchema ?? {};
                                const lineSchema = {
                                    type: "object",
                                    additionalProperties: false,
                                    required: ["id", "meta", "input", "output"],
                                    properties: {
                                        id: {
                                            type: "string",
                                            format: "uuid"
                                        },
                                        meta: {
                                            type: "object",
                                            additionalProperties: false,
                                            required: ["insertedAt", "processedAt", "state", "confidence", "validationResult"],
                                            properties: {
                                                insertedAt: {
                                                    type: "string",
                                                    format: "date-time"
                                                },
                                                processedAt: {
                                                    type: ["string", "null"],
                                                    format: "date-time"
                                                },
                                                state: {
                                                    type: "string"
                                                },
                                                confidence: {
                                                    type: ["number", "null"],
                                                    minimum: 0,
                                                    maximum: 1
                                                },
                                                validationResult: {
                                                    anyOf: [
                                                        {
                                                            type: "object",
                                                            additionalProperties: false,
                                                            required: ["passed"],
                                                            properties: {
                                                                passed: {
                                                                    type: "boolean"
                                                                },
                                                                reason: {
                                                                    type: ["string", "null"]
                                                                }
                                                            }
                                                        },
                                                        {
                                                            type: "null"
                                                        }
                                                    ]
                                                }
                                            }
                                        },
                                        input: {},
                                        output: {
                                            anyOf: [
                                                resultSchema,
                                                {
                                                    type: "null"
                                                }
                                            ]
                                        }
                                    }
                                };

                                copyToClipboard(JSON.stringify(lineSchema, null, 4));
                            });
                        }
                    })
                ),
                Dropdown("Advanced",
                    DropdownItem("Delete job", Icon("delete-bin-line"), {
                        onClick() {
                            confirmDanger({
                                message: "This deletes the job and all its items. This action is destructive and cannot be undone.",
                                buttonText: "Delete job",
                                operationText: "Deleting job...",
                                async onConfirm() {
                                    await api.deleteJson(`/api/v1/batch/workflows/${workflowId}/jobs/${jobId}`);
                                    page("/dashboard/batch");
                                }
                            });
                        }
                    })
                )
            )
        ),
        Section(
            TabControl([
                {
                    title: "Insights",
                    icon: Icon("bar-chart-2-line"),
                    contents: el.state.listening([jobData], () => {
                        const summary = jobData.value?.summary;
                        const progression = jobData.value?.progression || [];
                        const failed = (summary?.refused || 0) + (summary?.executionErrors || 0) + (summary?.validationErrors || 0) + (summary?.cancelled || 0);
                        const total = (summary?.pending || 0) + (summary?.finished || 0) + (summary?.refused || 0) +
                            (summary?.executionErrors || 0) + (summary?.validationErrors || 0) + (summary?.cancelled || 0);
                        const processed = (summary?.finished || 0) + failed;
                        const pending = summary?.pending || 0;
                        const chargedCost = Number(summary?.totalCost || 0);
                        const predictedCost = processed > 0 ? chargedCost + (chargedCost / processed * pending) : null;
                        const predictedDuration = summary?.restartedAt && processed > 0 && pending > 0
                            ? formatDuration(Math.max(1, (Date.now() - new Date(summary.restartedAt).getTime()) / 1000) / processed * pending)
                            : null;

                        return el("div",
                            Section(
                                Card({ class: "bg-card" },
                                    CardHeader("Progression"),
                                    CardContent(
                                        progression.length === 0
                                            ? MutedText("No processed items yet.")
                                            : AreaChart({
                                                height: 260,
                                                series: [
                                                    {
                                                        name: "Successful",
                                                        data: progression.map(point => ({
                                                            x: new Date(point.moment || point.Moment).getTime(),
                                                            y: point.finished || 0
                                                        }))
                                                    },
                                                    {
                                                        name: "Errors",
                                                        data: progression.map(point => ({
                                                            x: new Date(point.moment || point.Moment).getTime(),
                                                            y: point.failed || 0
                                                        }))
                                                    },
                                                    {
                                                        name: "Confidence",
                                                        data: progression.map(point => {
                                                            const confidence = point.averageConfidence ?? point.AverageConfidence;
                                                            return {
                                                                x: new Date(point.moment || point.Moment).getTime(),
                                                                y: confidence == null ? null : Number(confidence) * 100
                                                            };
                                                        })
                                                    },
                                                    {
                                                        name: "Refusal rate",
                                                        data: progression.map(point => {
                                                            const refusalRate = point.refusalRate ?? point.RefusalRate;
                                                            return {
                                                                x: new Date(point.moment || point.Moment).getTime(),
                                                                y: Number(refusalRate || 0) * 100
                                                            };
                                                        })
                                                    }
                                                ],
                                                xaxis: {
                                                    type: "datetime"
                                                },
                                                yaxis: [
                                                    {
                                                        seriesName: ["Successful", "Errors"],
                                                        min: 0,
                                                        title: { text: "Items" }
                                                    },
                                                    {
                                                        seriesName: ["Confidence", "Refusal rate"],
                                                        min: 0,
                                                        max: 100,
                                                        opposite: true,
                                                        title: { text: "Rates" },
                                                        labels: {
                                                            formatter: value => `${Math.round(value)}%`
                                                        }
                                                    }
                                                ],
                                                colors: ["#1a7f37", "#cf222e", "#0969da", "#8250df"]
                                            })
                                    )
                                )
                            ),
                            Section(
                                Card({ class: "bg-card" },
                                    CardHeader("Completion"),
                                    CardContent(
                                        SegmentBar({
                                            autosize: true,
                                            segments: [
                                                { label: "Pending", value: summary?.pending || 0, color: "#8c959f" },
                                                { label: "Running", value: summary?.running || 0, color: "#0969da" },
                                                { label: "Successful", value: summary?.finished || 0, color: "#1a7f37" },
                                                { label: "Failures", value: failed, color: "#cf222e" }
                                            ]
                                        }),
                                        el("div", {
                                            style: {
                                                display: "flex",
                                                gap: "24px",
                                                flexWrap: "wrap",
                                                marginTop: "16px"
                                            }
                                        },
                                            [
                                                ["Total", total],
                                                ["Successful", summary?.finished || 0],
                                                ["Failures", failed],
                                                ["Pending", summary?.pending || 0],
                                                ["Success rate", `${formatNumber(Number(summary?.successRate || 0) * 100)}%`],
                                                ["Refusal rate", `${formatNumber(Number(summary?.refusalRate || 0) * 100)}%`],
                                                ["Avg confidence", summary?.averageConfidence == null ? "-" : `${formatNumber(Number(summary.averageConfidence) * 100)}%`]
                                            ].map(([label, value]) =>
                                                el("div", { style: { minWidth: "130px" } },
                                                    el("div", typeof value === "number" ? formatNumber(value || 0) : value || "-", {
                                                        style: {
                                                            fontSize: "1.35rem"
                                                        }
                                                    }),
                                                    el("div", label, { class: "muted-text" })
                                                )
                                            )
                                        )
                                    )
                                )
                            ),
                            Section(
                                Card({ class: "bg-card" },
                                    CardHeader("Cost and time"),
                                    CardContent(
                                        el("div",
                                            {
                                                style: {
                                                    display: "flex",
                                                    gap: "24px",
                                                    flexWrap: "wrap"
                                                }
                                            },
                                            [
                                                ["Cost charged", formatCurrency(chargedCost), formatVeryPreciseCurrency(summary?.totalCost)],
                                                ["Predicted cost", predictedCost == null ? "-" : formatPreciseCurrency(Number(predictedCost || 0)), "Estimated total cost when all pending items are processed."],
                                                ["ETA", predictedDuration || "-", null],
                                                ["Elapsed time", runtimeLabel(summary), null]
                                            ].map(([label, value, title]) =>
                                                el("div", { style: { minWidth: "130px" }, title },
                                                    el("div", typeof value === "number" ? formatNumber(value || 0) : value || "-", {
                                                        style: {
                                                            fontSize: "1.35rem"
                                                        }
                                                    }),
                                                    el("div", label, { class: "muted-text" })
                                                )
                                            )
                                        )
                                    )
                                )
                            ),
                            Section(
                                Card({ class: "bg-card" },
                                    CardHeader("Latest processed items"),
                                    CardContent(
                                        Table(
                                            {
                                                classList: ["selectable"]
                                            },
                                            TableHeader(
                                                TableHeaderCell("Id"),
                                                TableHeaderCell("Processed"),
                                                TableHeaderCell("State"),
                                                TableHeaderCell("Confidence"),
                                                TableHeaderCell("Cost")
                                            ),
                                            TableBody(
                                                jobData.value?.latestItems?.map(item => {
                                                    const confidence = item.confidence ?? item.Confidence;
                                                    return TableRow(
                                                        {
                                                            onClick() {
                                                                viewItem(item.id);
                                                            }
                                                        },
                                                        TableCell(CopiableText(item.id ? "#" + item.id.slice(-6) : "-", item.id)),
                                                        TableCell(item.processedAt ? DateTimeLabel(item.processedAt) : MutedText("-")),
                                                        TableCell(StateBadge(item.state)),
                                                        TableCell(confidence == null ? MutedText("-") : `${formatNumber(Number(confidence) * 100)}%`),
                                                        TableCell(formatCurrency(Number(item.totalCost || 0)))
                                                    );
                                                }).nullWhenEmpty() || DefaultRow("No processed items yet.")
                                            )
                                        )
                                    )
                                )
                            )
                        );
                    })
                },
                {
                    title: "Processing list",
                    icon: Icon("list-check"),
                    contents: el.fragment(
                        Section(
                            Heading(
                                "Processing list",
                                "Items are processed in insertion order, with account plan priority and per-item priority applied by the worker."
                            )
                        ),
                        Section(
                            CommandBar(
                                FormControl("Add items",
                                    Dropdown("Insert data",
                                        DropdownItem("From text file", Icon("file-text-line"), {
                                            onClick() {
                                                displayFormControl({
                                                    title: "Import from text file",
                                                    message: "Select a line-delimited file. Blank lines are ignored.",
                                                    formControlItem: FileInput({ accept: ".jsonl,.txt,.csv" }),
                                                    validate: [
                                                        [x => x != null, "Attach a file before importing."]
                                                    ],
                                                    onConfirm(value) {
                                                        const formData = new FormData();
                                                        formData.append("mode", "lines");
                                                        formData.append("items", value);
                                                        insertItems(formData, "Importing processing list...");
                                                    }
                                                });
                                            }
                                        }),
                                        DropdownItem("From files", Icon("file-copy-2-line"), {
                                            onClick() {
                                                displayFormControl({
                                                    title: "Import from files",
                                                    message: "Select text files. Each file becomes one processing item.",
                                                    formControlItem: FileInput({ accept: ".txt,.json,.jsonl,.csv,.md,.xml,.html,.log,.yaml,.yml", multiple: true }),
                                                    validate: [
                                                        [x => x != null && x.length > 0, "Attach at least one file before importing."]
                                                    ],
                                                    onConfirm(value) {
                                                        const formData = new FormData();
                                                        formData.append("mode", "files");
                                                        for (const file of value)
                                                            formData.append("items", file);
                                                        insertItems(formData, "Importing files...");
                                                    }
                                                });
                                            }
                                        }),
                                        DropdownItem("From zip", Icon("folder-zip-line"), {
                                            onClick() {
                                                displayFormControl({
                                                    title: "Import from ZIP",
                                                    message: "Select a ZIP archive. Each text file inside it becomes one processing item.",
                                                    formControlItem: FileInput({ accept: ".zip" }),
                                                    validate: [
                                                        [x => x != null, "Attach a ZIP file before importing."]
                                                    ],
                                                    onConfirm(value) {
                                                        const formData = new FormData();
                                                        formData.append("mode", "zip");
                                                        formData.append("items", value);
                                                        insertItems(formData, "Importing ZIP...");
                                                    }
                                                });
                                            }
                                        }),
                                        DropdownDivider(),
                                        DropdownItem("Type text", Icon("keyboard-box-line"), {
                                            onClick() {
                                                displayFormControl({
                                                    title: "Type text",
                                                    message: "Insert a single processing item manually.",
                                                    dialogSize: "large",
                                                    formControlItem: TextArea(null, "", { rows: 12 }),
                                                    validate: [
                                                        [x => String(x || "").trim().length > 0, "Type text before inserting."]
                                                    ],
                                                    onConfirm(value) {
                                                        const formData = new FormData();
                                                        formData.append("mode", "text");
                                                        formData.append("text", value);
                                                        insertItems(formData, "Inserting item...");
                                                    }
                                                });
                                            }
                                        }),
                                        DropdownDivider(),
                                        DropdownItem("Permutation generator", Icon("play-list-2-line"), {
                                            onClick() {
                                                displayFormControl({
                                                    title: "Permutation generator",
                                                    dialogSize: "large",
                                                    formControlItem: PermutationGenerator(),
                                                    onConfirm(value) {
                                                        const formData = new FormData();
                                                        formData.append("mode", "textlines");
                                                        formData.append("textlines", value);
                                                        insertItems(formData, "Inserting items...");
                                                    }
                                                })
                                            }
                                        })
                                    )
                                ),
                                FormControl("Manage items",
                                    ButtonRow(
                                        Dropdown("Retry",
                                            DropdownItem("All failed", Icon("refresh-line"), {
                                                onClick() {
                                                    retryItems("errors");
                                                }
                                            }),
                                            DropdownItem("Low confidence", Icon("arrow-down-circle-line"), {
                                                onClick() {
                                                    retryItems("low-confidence");
                                                }
                                            }),
                                            DropdownDivider(),
                                            DropdownItem("Execution error", Icon("error-warning-line"), {
                                                onClick() {
                                                    retryItems("execution-error");
                                                }
                                            }),
                                            DropdownItem("Validation error", Icon("shield-check-line"), {
                                                onClick() {
                                                    retryItems("validation-error");
                                                }
                                            })
                                        ),
                                        Dropdown("Remove",
                                            DropdownItem("Pending items", Icon("time-line"), {
                                                onClick() {
                                                    removeItems("pending", "This removes all pending non-running items from the job.");
                                                }
                                            }),
                                            DropdownItem("Successful items", Icon("checkbox-circle-line"), {
                                                onClick() {
                                                    removeItems("finished", "This removes successful non-running items from the job.");
                                                }
                                            }),
                                            DropdownItem("Failed items", Icon("error-warning-line"), {
                                                onClick() {
                                                    removeItems("errors", "This removes refused, execution error and validation error items.");
                                                }
                                            }),
                                            DropdownDivider(),
                                            DropdownItem("All non-running items", Icon("delete-bin-line"), {
                                                onClick() {
                                                    removeItems("all", "This removes every non-running item from the job.");
                                                }
                                            })
                                        ),
                                    )
                                ),
                                TextInput("batchItemFilter", "Search input...", {
                                    value: itemFilter.value,
                                    onInput(event) {
                                        throttledCall(async () => {
                                            itemFilter.value = event.target.value;
                                            await reloadItems();
                                        }, 250);
                                    }
                                }),
                                FormControl("View",
                                    ButtonRow(
                                        Select({
                                            value: stateFilter.value,
                                            items: BatchItemStates,
                                            onChange(event) {
                                                stateFilter.value = event.target.value;
                                                reloadItems();
                                            }
                                        }),
                                        Select({
                                            value: itemSort.value,
                                            items: [
                                                { value: "inserted-desc", text: "Inserted (desc)" },
                                                { value: "inserted-asc", text: "Inserted (asc)" },
                                                { value: "processed-desc", text: "Processed (desc)" },
                                                { value: "processed-asc", text: "Processed (asc)" }
                                            ],
                                            onChange(event) {
                                                itemSort.value = event.target.value;
                                                reloadItems();
                                            }
                                        })
                                    )
                                )
                            )
                        ),
                        Section(
                            Card(
                                { class: "bg-card" },
                                CardHeader("Items"),
                                CardContent(
                                    el.state.listening([items], () =>
                                        Table(
                                            {
                                                pagination: {
                                                    perPage: 100
                                                }
                                            },
                                            TableHeader(
                                                TableHeaderCell("Id"),
                                                TableHeaderCell("Inserted"),
                                                TableHeaderCell("State"),
                                                TableHeaderCell("Priority"),
                                                TableHeaderCell("Input"),
                                                TableHeaderCell("Confidence"),
                                                TableHeaderCell("Cost"),
                                                TableHeaderActionCell("Actions")
                                            ),
                                            TableBody(
                                                items.value?.map(item => {
                                                    const confidence = item.confidence ?? item.Confidence;
                                                    const isRunning = item.isRunning === true;
                                                    const isPending = item.state[0] === "Pending";
                                                    const canChangePendingItem = isPending && !isRunning;
                                                    return TableRow(
                                                        TableCell(CopiableText(item.id ? "#" + item.id.slice(-6) : "-", item.id)),
                                                        TableCell(DateTimeLabel(item.insertedAt)),
                                                        TableCell(StateBadge(item.state)),
                                                        TableCell(item.priority),
                                                        TableCell(item.input || ""),
                                                        TableCell(confidence == null ? MutedText("-") : `${formatNumber(Number(confidence) * 100)}%`),
                                                        TableCell(formatCurrency(Number(item.totalCost || 0))),
                                                        TableActionCell(
                                                            ButtonRow(
                                                                Button(Icon("file-list-3-line"), "View",
                                                                    {
                                                                        onClick() {
                                                                            viewItem(item.id);
                                                                        }
                                                                    }
                                                                )
                                                            ),
                                                            canChangePendingItem && Dropdown(null,
                                                                DropdownDivider(),
                                                                DropdownItem("Move up", Icon("arrow-up-line"), {
                                                                    onClick() {
                                                                        runOperation("Updating item priority...", async () => {
                                                                            await api.patchJson(`/api/v1/batch/workflows/${workflowId}/jobs/${jobId}/items/${item.id}`, {
                                                                                priority: 0
                                                                            });
                                                                            await reloadItems();
                                                                        });
                                                                    }
                                                                }),
                                                                DropdownItem("Move down", Icon("arrow-down-line"), {
                                                                    onClick() {
                                                                        runOperation("Updating item priority...", async () => {
                                                                            await api.patchJson(`/api/v1/batch/workflows/${workflowId}/jobs/${jobId}/items/${item.id}`, {
                                                                                priority: 200
                                                                            });
                                                                            await reloadItems();
                                                                        });
                                                                    }
                                                                }),
                                                                DropdownDivider(),
                                                                DropdownItem("Cancel", Icon("close-circle-line"), {
                                                                    onClick() {
                                                                        confirmDanger({
                                                                            message: "This removes the item from the processing list. Running inference is not interrupted.",
                                                                            buttonText: "Cancel item",
                                                                            operationText: "Cancelling item...",
                                                                            async onConfirm() {
                                                                                await api.patchJson(`/api/v1/batch/workflows/${workflowId}/jobs/${jobId}/items/${item.id}`, {
                                                                                    action: "cancel"
                                                                                });
                                                                                await reloadAll();
                                                                            }
                                                                        });
                                                                    }
                                                                })
                                                            )
                                                        )
                                                    );
                                                }).nullWhenEmpty() || DefaultRow("No items found.")
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                },
                {
                    title: "Events",
                    icon: Icon("history-line"),
                    contents: el.state.listening([jobData], () =>
                        Section(
                            Card(
                                { class: "bg-card" },
                                CardHeader("Job events"),
                                CardContent(
                                    Table(
                                        TableHeader(
                                            TableHeaderCell("Moment"),
                                            TableHeaderCell("Type"),
                                            TableHeaderCell("Description")
                                        ),
                                        TableBody(
                                            jobData.value?.job?.updates?.map(update =>
                                                TableRow(
                                                    TableCell(DateTimeLabel(update.moment)),
                                                    TableCell(update.eventType),
                                                    TableCell(update.description)
                                                )
                                            ).nullWhenEmpty() || DefaultRow("No events found.")
                                        )
                                    )
                                )
                            )
                        )
                    )
                }
            ], {
                type: "fixed"
            })
        )
    );
}
